import {
  type ApolloClient,
  type DocumentNode,
  type TypedDocumentNode,
  type OperationVariables,
  useMutation,
  type DefaultContext,
  useLazyQuery,
  type MutationHookOptions,
} from '@apollo/client';
import { useEffect, useRef, useState } from 'react';

const DEFAULT_MAX_RETRIES = 20;
const DEFAULT_DELAY_BETWEEN_ATTEMPTS = 2000;
const DEFAULT_MAX_BACKOFF_DELAY = 30000;

/**
 * Represents the status of an export file operation.
 */
export enum ExportStatus {
  /** The export has completed successfully and the file is ready */
  Success = 'Success',
  /** The export is still being processed */
  Processing = 'Processing',
  /** The export has failed */
  Failed = 'Failed',
}

type GetFileData = {
  status: ExportStatus;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & Record<string, any>;

export type UseExportFileProps<
  TExportMutation,
  TExportMutationVariables,
  TGetExportFileQuery,
  TGetExportFileQueryVariables,
> = {
  exportMutationDocument:
    | DocumentNode
    | TypedDocumentNode<TExportMutation, TExportMutationVariables>;
  getExportFileQueryDocument:
    | DocumentNode
    | TypedDocumentNode<TGetExportFileQuery, TGetExportFileQueryVariables>;
  fetchPolicy?: MutationHookOptions['fetchPolicy'];
  clientInstance: ApolloClient<object>;
  context?: DefaultContext;
  events?: {
    onExportSuccess?: (data: NoInfer<TExportMutation>) => void;
    onExportFailed?: (error: Error) => void;
    onGetFileSuccess?: (data: NoInfer<TGetExportFileQuery>) => void;
    onGetFileFailed?: (error: Error) => void;
    onStartExport?: () => void;
  };
  extractExportDataForQueryVariables: (
    data: NoInfer<TExportMutation>,
  ) => TGetExportFileQueryVariables;
  extractGetFileData: (data: NoInfer<TGetExportFileQuery>) => GetFileData;
  maxRetries?: number;
  delayTimeBetweenAttempts?: number;
  useExponentialBackoff?: boolean;
  maxBackoffDelay?: number;
};

/**
 * A React hook for handling async file exports with automatic polling and retry logic.
 *
 * This hook manages a two-step export process:
 * 1. Triggers an export mutation to initiate the file generation
 * 2. Polls a query endpoint until the file is ready (Success), fails (Failed), or exceeds max retries
 *
 * @template TExportMutation - The type of the export mutation response
 * @template TExportMutationVariables - The type of variables for the export mutation
 * @template TGetExportFileQuery - The type of the get file query response
 * @template TGetExportFileQueryVariables - The type of variables for the get file query
 *
 * @param props - Configuration options for the hook
 * @param props.exportMutationDocument - GraphQL mutation document to trigger the export
 * @param props.getExportFileQueryDocument - GraphQL query document to check export status/get file
 * @param props.clientInstance - Apollo Client instance to use for requests
 * @param props.context - Optional Apollo context for requests
 * @param props.fetchPolicy - Optional fetch policy (defaults to 'network-only')
 * @param props.events - Optional event callbacks for export lifecycle
 * @param props.events.onExportSuccess - Called when export mutation succeeds
 * @param props.events.onExportFailed - Called when export mutation fails
 * @param props.events.onGetFileSuccess - Called when file is ready
 * @param props.events.onGetFileFailed - Called when file retrieval fails or exceeds retries
 * @param props.events.onStartExport - Called when export process starts
 * @param props.extractExportDataForQueryVariables - Function to extract query variables from mutation response
 * @param props.extractGetFileData - Function to extract status and file data from query response
 * @param props.maxRetries - Maximum polling attempts (defaults to 20)
 * @param props.delayTimeBetweenAttempts - Base delay between polls in ms (defaults to 2000). When exponential backoff is enabled, this is the initial delay that doubles on each attempt.
 * @param props.useExponentialBackoff - Whether to use exponential backoff for polling delays (defaults to true). When enabled, the delay doubles after each attempt (2s → 4s → 8s → ...) up to `maxBackoffDelay`.
 * @param props.maxBackoffDelay - Maximum delay in ms when using exponential backoff (defaults to 30000)
 *
 * @returns A tuple containing:
 * - `exportFile` - Function to trigger the export with mutation variables
 * - `state` - Object with `data` (query result), `loading` (boolean), and `error` (Error | null)
 *
 * @example
 * ```tsx
 * const [exportFile, { data, loading, error }] = useExportFile({
 *   exportMutationDocument: EXPORT_REPORT_MUTATION,
 *   getExportFileQueryDocument: GET_EXPORT_FILE_QUERY,
 *   clientInstance: apolloClient,
 *   extractExportDataForQueryVariables: (data) => ({ fileId: data.exportReport.id }),
 *   extractGetFileData: (data) => ({
 *     status: data.getExportFile.status,
 *     url: data.getExportFile.url,
 *   }),
 *   events: {
 *     onGetFileSuccess: (data) => window.open(data.getExportFile.url),
 *     onGetFileFailed: (error) => toast.error(error.message),
 *   },
 * });
 *
 * // Trigger export
 * exportFile({ reportId: '123', format: 'xlsx' });
 * ```
 */
export function useExportFile<
  TExportMutation,
  TExportMutationVariables extends OperationVariables,
  TGetExportFileQuery,
  TGetExportFileQueryVariables extends OperationVariables,
>({
  clientInstance,
  exportMutationDocument,
  getExportFileQueryDocument,
  fetchPolicy,
  context,
  events,
  maxRetries = DEFAULT_MAX_RETRIES,
  extractExportDataForQueryVariables,
  extractGetFileData,
  delayTimeBetweenAttempts = DEFAULT_DELAY_BETWEEN_ATTEMPTS,
  useExponentialBackoff = true,
  maxBackoffDelay = DEFAULT_MAX_BACKOFF_DELAY,
}: UseExportFileProps<
  TExportMutation,
  TExportMutationVariables,
  TGetExportFileQuery,
  TGetExportFileQueryVariables
>) {
  const [queryVariables, setQueryVariables] =
    useState<TGetExportFileQueryVariables | null>(null);
  const [result, setResult] = useState<NoInfer<TGetExportFileQuery> | null>(
    null,
  );
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);

  const attemptsRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const [getFile] = useLazyQuery(getExportFileQueryDocument, {
    client: clientInstance,
    context,
    fetchPolicy: fetchPolicy ?? 'network-only',
    onError: error => {
      handleError(error);
    },
    onCompleted(data) {
      if (!queryVariables) {
        return;
      }

      const getFileData = extractGetFileData(data);

      const exceedRetryTime = attemptsRef.current >= maxRetries;

      if (getFileData.status === ExportStatus.Failed || exceedRetryTime) {
        handleError();
        return;
      }

      if (getFileData.status === ExportStatus.Processing) {
        const currentAttempt = attemptsRef.current;
        attemptsRef.current += 1;

        const delay = useExponentialBackoff
          ? Math.min(
              delayTimeBetweenAttempts * Math.pow(2, currentAttempt),
              maxBackoffDelay,
            )
          : delayTimeBetweenAttempts;

        timeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            getFile({
              variables: queryVariables,
            });
          }
        }, delay);

        return;
      }

      if (getFileData.status === ExportStatus.Success) {
        setResult(data);
        cleanUp();
        events?.onGetFileSuccess?.(data);
      }
    },
  });

  const handleError = (err?: Error) => {
    const errorToSet =
      err ?? new Error('Failed to get exported file. Please try again');
    setError(errorToSet);
    events?.onGetFileFailed?.(errorToSet);
    cleanUp();
  };

  const cleanUp = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    attemptsRef.current = 0;
    setResult(null);
    setQueryVariables(null);
    setLoading(false);
  };

  const [exportFileFn] = useMutation(exportMutationDocument, {
    client: clientInstance,
    context,
    fetchPolicy: fetchPolicy ?? 'network-only',
    onError: err => {
      setError(err);
      events?.onExportFailed?.(err);
      cleanUp();
    },
    onCompleted: data => {
      events?.onExportSuccess?.(data);

      const queryVariables = extractExportDataForQueryVariables(data);

      setQueryVariables(queryVariables);

      getFile({
        variables: queryVariables,
      });
    },
  });

  const exportFile = (exportMutationVariables: TExportMutationVariables) => {
    // Prevent multiple concurrent exports
    if (loading) {
      return;
    }

    setError(null);
    setLoading(true);

    events?.onStartExport?.();

    exportFileFn({
      variables: exportMutationVariables,
    });
  };

  return [exportFile, { data: result, loading, error }] as [
    typeof exportFile,
    {
      data: typeof result;
      loading: boolean;
      error: Error | null;
    },
  ];
}
